import React from 'react';
import { Box, Typography } from '@mui/material';
import { CombinedNumeric, NumericQuestionInsight } from '../insightsTypes';
import AverageScoreBars from '@/components/globalPrompts/insights/charts/AverageScoreBars';
import DonutChart from '@/components/common/charts/DonutChart';
import OverviewShell from './slices/OverviewShell';
import MetricHeading from './slices/MetricHeading';
import NoInsightsOverview from './slices/NoInsightsOverview';
import { COMBINED_BAR_COLOR, COMBINED_TEXT_COLOR, NEUTRAL_TRACK } from '../insightsTheme';
import { getQuestionLabel } from '../shared/getQuestionLabel';

// Global numeric questions use a fixed 1–10 scale (see server generation default).
const SCALE_MAX = 10;

// Describes combineNumericInsights on the server (services/v2/helpers/combineInsights.ts).
// Keep the two in step if the formula changes.
const WEIGHTED_AVERAGE_INFO = 'The average of every single answer, so questions with more answers count for more.';

/** "1 question", "5 questions". */
const pluralize = (count: number, word: string): string => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

interface NumericCategoryOverviewProps {
    // Server-computed rollup for all numeric questions in the section.
    combined: CombinedNumeric;
    // Used for the "Average score by question" bars (per-question reshape, not aggregation).
    questions: NumericQuestionInsight[];
}

const NumericCategoryOverview: React.FC<NumericCategoryOverviewProps> = ({ combined, questions }) => {
    if (combined.overall.responseCount === 0) {
        return <NoInsightsOverview />;
    }

    // Only questions with responses carry an insight; skip new/unanswered ones.
    // AverageScoreBars sorts by average and keeps the top 5 (limit prop below).
    const byQuestion = questions
        .filter((question) => question.overall.responseCount > 0)
        .map((question) => ({
            // Visible bar label: short title if set, otherwise truncated question text.
            label: getQuestionLabel(question),
            // Hover tooltip: always the full question text.
            questiontext: question.questionText,
            average: question.overall.average,
            responses: question.overall.responseCount,
        }));

    const answeredCount = questions.filter((question) => question.overall.responseCount > 0).length;
    const averagePct = (combined.overall.average / SCALE_MAX) * 100;

    // Answers with no project land in one "Unassigned" row. It is left out of the count so
    // this reads the same number as the "Projects analyzed" tile, which also counts only
    // real projects.
    const projectCount = combined.perProject.filter((project) => project.campaignId !== null).length;

    return (
        <OverviewShell>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 4, alignItems: 'center' }}>
                <Box>
                    <MetricHeading info={WEIGHTED_AVERAGE_INFO}>Weighted average</MetricHeading>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1.5 }}>
                        <DonutChart
                            slices={[
                                {
                                    label: 'Average',
                                    percentage: averagePct,
                                    count: combined.overall.responseCount,
                                    color: COMBINED_BAR_COLOR,
                                },
                                { label: 'Remaining', percentage: 100 - averagePct, count: 0, color: NEUTRAL_TRACK },
                            ]}
                            centerValue={combined.overall.average.toFixed(1)}
                            centerLabel={`/ ${SCALE_MAX} avg`}
                            centerColor={COMBINED_TEXT_COLOR}
                            size={132}
                            ringWidth={18}
                            hideLegend
                        />
                        <Typography variant="body2" color="text.secondary">
                            {combined.overall.responseCount.toLocaleString()} responses across {pluralize(answeredCount, 'question')}
                            {projectCount > 0 && ` in ${pluralize(projectCount, 'project')}`}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 4 } }}>
                    <MetricHeading>Top 5 questions by Average score </MetricHeading>
                    <Box sx={{ mt: 1.5 }}>
                        {/* Combined view: one calm blue, no good/bad colour coding. */}
                        <AverageScoreBars
                            rows={byQuestion}
                            max={SCALE_MAX}
                            limit={5}
                            barColor={COMBINED_BAR_COLOR}
                            valueColor={COMBINED_TEXT_COLOR}
                        />
                    </Box>
                </Box>
            </Box>
        </OverviewShell>
    );
};

export default NumericCategoryOverview;
